import { useMemo, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import RetentionPreviewDialog from "../components/RetentionPreviewDialog";
import { useAuth } from "../contexts/AuthContext";
import { PushNotificationEntry, NotificationChannel, NotificationStatus, usePushNotifications } from "../hooks/usePushNotifications";
import { ArrowPathIcon, EyeIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";

const statusStyles: Record<NotificationStatus, string> = {
    live: "bg-green-100 text-green-800",
    dryRun: "bg-blue-100 text-blue-800",
    planned: "bg-gray-100 text-gray-600",
    awaitingCopy: "bg-orange-100 text-orange-800",
};

const statusLabels: Record<NotificationStatus, string> = {
    live: "Live",
    dryRun: "Dry run",
    planned: "Not live",
    awaitingCopy: "Awaiting copy",
};

const channelLabels: Record<NotificationChannel, string> = {
    push: "Push",
    banner: "Banner",
    inApp: "Bell",
    email: "Email",
};

const modeLabels = {
    off: "Off",
    dryRun: "Dry run (logged, not delivered)",
    live: "Live",
};

const testAccountsStyle = "bg-teal-100 text-teal-800";

/** Status badge: retention rows can also be switched on from the settings document. */
const displayStatus = (n: PushNotificationEntry) => {
    if (n.liveFor === "testAccounts") return { label: "Live (test accounts)", style: testAccountsStyle };
    const status: NotificationStatus = n.liveFor === "everyone" ? "live" : n.status;
    return { label: statusLabels[status], style: statusStyles[status] };
};

const PushNotificationsPage: React.FC = () => {
    const { notifications, categories, retentionMode, testAccountCount, loading, error, fetchCatalog } = usePushNotifications();
    const { userData } = useAuth();
    const isSuperAdmin = userData?.superAdmin === true;
    const [group, setGroup] = useState<string>("All");
    const [selected, setSelected] = useState<PushNotificationEntry | null>(null);
    const [showPreview, setShowPreview] = useState(false);

    const groups = useMemo(() => ["All", ...Array.from(new Set(notifications.map((n) => n.group)))], [notifications]);
    const visible = group === "All" ? notifications : notifications.filter((n) => n.group === group);
    const liveCount = notifications.filter((n) => n.status === "live" || n.liveFor !== null).length;
    const sent7d = notifications.reduce((sum, n) => sum + n.sent7d, 0);

    const label = (n: PushNotificationEntry) => (n.row ? `${n.row}. ${n.name}` : n.name);

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6 md:h-[calc(100vh-64px)] overflow-hidden">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl md:text-2xl font-heading text-gray-400">Push Notifications</h1>
                        <p className="text-xs text-gray-400 mt-1">
                            Read-only list of every notification the backend sends, with its trigger, conditions and copy.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isSuperAdmin && (
                            <button
                                onClick={() => setShowPreview(true)}
                                className="flex items-center gap-1 px-3 py-2 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                                title="Which retention message a user would get today, and why"
                            >
                                <UserIcon className="w-4 h-4" />
                                Preview for a user
                            </button>
                        )}
                        <button
                            onClick={fetchCatalog}
                            disabled={loading}
                            className="p-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50"
                            title="Refresh"
                        >
                            <ArrowPathIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="w-full grid grid-cols-3 gap-2 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">{liveCount} / {notifications.length}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Live notifications</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">{sent7d}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Delivered, last 7 days</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-base md:text-lg text-gray-400 font-bold">{modeLabels[retentionMode]}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">
                            Retention notifications{testAccountCount > 0 && ` · ${testAccountCount} test account${testAccountCount === 1 ? "" : "s"}`}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 shrink-0">
                    {groups.map((g) => (
                        <button
                            key={g}
                            onClick={() => setGroup(g)}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors ${group === g ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        >
                            {g}
                        </button>
                    ))}
                </div>

                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_3fr_1.2fr_0.8fr_0.8fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                        <div>Notification</div>
                        <div className="hidden md:block">Trigger</div>
                        <div className="hidden md:block">Channels</div>
                        <div className="hidden md:block">Sent 7d / 30d</div>
                        <div>Status</div>
                        <div className="hidden md:block">Details</div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                        {loading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">Loading notifications...</div>
                        ) : error ? (
                            <div className="flex items-center justify-center h-32 text-red-500 text-sm">Could not load notifications: {error.message}</div>
                        ) : visible.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">No notifications found</div>
                        ) : (
                            visible.map((item) => (
                                <div
                                    key={item.id}
                                    className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_3fr_1.2fr_0.8fr_0.8fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer md:cursor-default"
                                    onClick={() => {
                                        if (window.innerWidth < 768) setSelected(item);
                                    }}
                                >
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-sm text-gray-600 truncate">{label(item)}</span>
                                        <span className="text-xs text-gray-400 truncate">{item.title || item.body || item.group}</span>
                                    </div>

                                    <div className="hidden md:block min-w-0">
                                        <p className="text-sm text-gray-600 line-clamp-2">{item.trigger}</p>
                                    </div>

                                    <div className="hidden md:flex flex-wrap gap-1">
                                        {item.channels.map((c) => (
                                            <span key={c} className="inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full bg-gray-100 text-gray-600">
                                                {channelLabels[c]}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="hidden md:block text-sm text-gray-600">
                                        {item.sent7d} / {item.sent30d}
                                        {item.dryRun7d > 0 && <span className="block text-[10px] text-blue-600">{item.dryRun7d} dry run (7d)</span>}
                                    </div>

                                    <div className="flex items-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${displayStatus(item).style}`}>
                                            {displayStatus(item).label}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-end gap-1 md:gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelected(item); }}
                                            className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                            title="View details"
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selected && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                                <h2 className="text-lg font-heading">{label(selected)}</h2>
                                <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-full">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="px-4 sm:px-6 py-4 space-y-4">
                                {(selected.title || selected.body) && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Message</label>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            {selected.title && <p className="text-sm font-semibold">{selected.title}</p>}
                                            {selected.body && <p className="text-sm whitespace-pre-line">{selected.body}</p>}
                                        </div>
                                    </div>
                                )}
                                {selected.buttons && selected.buttons.length > 0 && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Buttons</label>
                                        <ul className="text-sm space-y-1">
                                            {selected.buttons.map((b) => (
                                                <li key={b.label}>[{b.label}] → app: <code>{b.route}</code>, web: <code>{b.url}</code></li>
                                            ))}
                                            {selected.buttonsIfTrialUsed?.map((b) => (
                                                <li key={`trial-${b.label}`}>Trial already used: [{b.label}] → <code>{b.route}</code></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Trigger</label>
                                    <p className="text-sm">{selected.trigger}</p>
                                </div>
                                {selected.conditions.length > 0 && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Conditions</label>
                                        <ul className="text-sm list-disc pl-5 space-y-1">
                                            {selected.conditions.map((c) => <li key={c}>{c}</li>)}
                                        </ul>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Channels</label>
                                        <p className="text-sm">{selected.channels.map((c) => channelLabels[c]).join(", ")}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Status</label>
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full ${displayStatus(selected).style}`}>
                                            {displayStatus(selected).label}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">User switch</label>
                                        <p className="text-sm">
                                            {!selected.userSetting
                                                ? "None"
                                                : selected.category
                                                    ? `${categories[selected.category] ?? selected.category} (push only)`
                                                    : selected.userSetting}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Daily / weekly limit</label>
                                        <p className="text-sm">{selected.countsTowardsLimit ? "Counts" : "Exempt"}</p>
                                    </div>
                                    {selected.priority !== undefined && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Priority</label>
                                            <p className="text-sm">{selected.priority + 1} (1 = highest)</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Sent (7d / 30d)</label>
                                        <p className="text-sm">{selected.sent7d} / {selected.sent30d}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Sent by</label>
                                    <code className="text-xs sm:text-sm bg-gray-100 px-2 py-1 rounded block overflow-x-auto">{selected.source}</code>
                                </div>
                                {selected.notes && selected.notes.length > 0 && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Notes</label>
                                        <ul className="text-sm list-disc pl-5 space-y-1">
                                            {selected.notes.map((n) => <li key={n}>{n}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t">
                                <button
                                    onClick={() => setSelected(null)}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showPreview && (
                    <RetentionPreviewDialog notifications={notifications} onClose={() => setShowPreview(false)} />
                )}
            </div>
        </DashboardWrapper>
    );
};

export default PushNotificationsPage;
